import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { formatDistanceToNow, format } from "date-fns";
import {
  ArrowLeft, Play, Trash2, Loader2, Pause, PlayCircle, Pencil, X,
  CheckCircle2, XCircle, BellRing, Mail, MessageSquare, Webhook, Radio,
} from "lucide-react";
import { WorkspaceNav } from "@/components/WorkspaceNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertMethodFields } from "@/components/alerts/AlertMethodFields";
import { ScheduleForm, toSchedulePayload } from "@/components/reports/ScheduleForm";
import { PromoteSignalDialog } from "@/components/signals/PromoteSignalDialog";
import { ALERTS } from "@/constants/testIds";
import { METHODS, DEFAULT_CONFIG, methodLabel, describeConfig, SEVERITY } from "@/lib/alerts";
import api from "@/lib/api";

const CHANNEL_ICON = { email: Mail, slack: MessageSquare, webhook: Webhook };

function rel(ts) { try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return "—"; } }
function abs(ts) { try { return format(new Date(ts), "MMM d, HH:mm"); } catch { return "—"; } }

function SeverityBadge({ severity }) {
  const s = SEVERITY[severity] || SEVERITY.low;
  return <Badge variant="outline" className={`text-[10px] ${s.cls}`}>{s.label}</Badge>;
}

function RunRow({ run }) {
  const Channel = (run.delivery_status?.[0]?.channel && CHANNEL_ICON[run.delivery_status[0].channel]) || null;
  return (
    <div data-testid={ALERTS.runRow} className="border-b border-border/40 px-4 py-3 last:border-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm">
          {run.breached ? <XCircle className="h-4 w-4 text-red-400" /> : <CheckCircle2 className="h-4 w-4 text-emerald-400" />}
          <span>{abs(run.ran_at)}</span>
          {run.breached ? <SeverityBadge severity={run.severity} /> : <span className="text-xs text-muted-foreground">normal</span>}
          <span className="text-xs text-muted-foreground">· {run.detail}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {run.value != null && <span>value {Number(run.value).toLocaleString()}</span>}
          {run.baseline != null && <span>· base {Number(run.baseline).toLocaleString()}</span>}
          {run.delivered && Channel && (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-400">
              <Channel className="h-3 w-3" /> sent
            </span>
          )}
        </div>
      </div>
      {run.breached && run.chart_png && (
        <img src={`data:image/png;base64,${run.chart_png}`} alt="evidence"
          className="mt-2 max-h-48 rounded-md border border-border/50" />
      )}
    </div>
  );
}

export default function AlertDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [runs, setRuns] = useState([]);
  const [testing, setTesting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [promoteOpen, setPromoteOpen] = useState(false);

  const load = useCallback(async () => {
    const [{ data: a }, { data: r }] = await Promise.all([
      api.get(`/alerts/${id}`),
      api.get(`/alerts/${id}/runs`),
    ]);
    setAlert(a);
    setRuns(r);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    setDraft({
      method: alert.method,
      config: alert.config || DEFAULT_CONFIG[alert.method],
      severity: alert.severity_override || "auto",
      cooldown: alert.cooldown_minutes ?? 30,
      schedule: {
        cron: alert.cron, timezone: alert.timezone || "UTC", delivery: alert.delivery,
        recipients: (alert.recipients || []).join(", "),
        slackUrl: alert.slack_webhook_url || "", webhookUrl: alert.webhook_url || "",
      },
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const sp = toSchedulePayload(draft.schedule);
      await api.patch(`/alerts/${id}`, {
        method: draft.method,
        config: draft.config,
        cron: sp.cron,
        timezone: sp.timezone,
        delivery: sp.delivery,
        recipients: sp.recipients,
        slack_webhook_url: sp.slack_webhook_url,
        webhook_url: sp.webhook_url,
        severity_override: draft.severity === "auto" ? null : draft.severity,
        cooldown_minutes: Number(draft.cooldown) || 30,
      });
      toast.success("Alert updated");
      setEditing(false);
      await load();
    } catch (e) {
      toast.error("Couldn't update", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const testNow = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/alerts/${id}/test`);
      if (data.breached) {
        toast.error(`Breach detected (${data.severity})`, { description: data.detail });
      } else {
        toast.success("Evaluated — within normal range", { description: data.detail });
      }
      await load();
    } finally {
      setTesting(false);
    }
  };

  const togglePause = async () => {
    await api.patch(`/alerts/${id}`, { active: !alert.active });
    toast.success(alert.active ? "Alert paused" : "Alert resumed");
    await load();
  };

  const remove = async () => {
    await api.delete(`/alerts/${id}`);
    toast.success("Alert deleted");
    navigate("/alerts");
  };

  if (!alert) {
    return (
      <div className="min-h-screen bg-background">
        <WorkspaceNav />
        <div className="px-6 py-20 text-center text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  const Channel = CHANNEL_ICON[alert.delivery] || Webhook;
  const latestBreach = runs.find((r) => r.breached) || null;
  const promoteEvidence = latestBreach ? {
    value: latestBreach.value,
    baseline: latestBreach.baseline,
    deviation: (latestBreach.value != null && latestBreach.baseline != null)
      ? latestBreach.value - latestBreach.baseline : null,
    sql: latestBreach.sql,
    points: latestBreach.points,
    chart_png_b64: latestBreach.chart_png,
  } : {};

  return (
    <div data-testid={ALERTS.detailRoot} className="min-h-screen bg-background text-foreground">
      <WorkspaceNav />
      <main className="mx-auto w-full max-w-6xl px-6 py-8">
        <Link to="/alerts" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3.5 w-3.5" /> Alerts
        </Link>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <BellRing className="h-5 w-5 text-accent" />
              <h1 className="truncate text-2xl font-bold tracking-tight sm:text-3xl">{alert.name}</h1>
              {!alert.active && <Badge variant="outline" className="text-[10px] text-amber-400 border-amber-500/40">paused</Badge>}
            </div>
            <p className="mt-1.5 text-sm text-muted-foreground">{alert.prompt}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button data-testid={ALERTS.testNow} onClick={testNow} disabled={testing}
              className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
              {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Play className="mr-2 h-4 w-4" />}
              Test now
            </Button>
            <Button variant="outline" data-testid={ALERTS.pauseToggle} onClick={togglePause} className="rounded-full">
              {alert.active ? <Pause className="mr-2 h-4 w-4" /> : <PlayCircle className="mr-2 h-4 w-4" />}
              {alert.active ? "Pause" : "Resume"}
            </Button>
            <Button variant="outline" onClick={() => setPromoteOpen(true)} disabled={!latestBreach}
              title={latestBreach ? "Promote latest breach to a signal" : "No breach to promote yet"}
              className="rounded-full border-accent/40 text-accent hover:bg-accent/10">
              <Radio className="mr-2 h-4 w-4" /> Promote
            </Button>
            <Button variant="outline" data-testid={ALERTS.editToggle} onClick={editing ? () => setEditing(false) : startEdit} className="rounded-full">
              {editing ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid={ALERTS.deleteAlert} className="rounded-full border-red-500/30 text-red-400 hover:bg-red-500/10">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="font-mono">
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this alert?</AlertDialogTitle>
                  <AlertDialogDescription>This removes the alert and its run history. Can’t be undone.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={remove} className="bg-red-500 hover:bg-red-600">Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="space-y-8 lg:col-span-2">
            <section>
              <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Latest evaluation</h2>
              <div className="rounded-xl border border-border/60 bg-card/30 p-4">
                {alert.last_run?.chart_png ? (
                  <>
                    <img src={`data:image/png;base64,${alert.last_run.chart_png}`} alt="latest"
                      className="w-full rounded-md border border-border/50" />
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                      {alert.last_run.breached
                        ? <><SeverityBadge severity={alert.last_run.severity} /><span className="text-red-400">{alert.last_run.detail}</span></>
                        : <span className="text-emerald-400">Within normal range — {alert.last_run.detail}</span>}
                    </div>
                  </>
                ) : (
                  <div className="py-12 text-center text-sm text-muted-foreground">Run “Test now” to evaluate this metric.</div>
                )}
              </div>
            </section>

            <section>
              <h2 className="mb-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Run history</h2>
              <div className="rounded-xl border border-border/60 bg-card/30">
                {runs.length ? runs.map((run) => <RunRow key={run.id} run={run} />)
                  : <div className="px-4 py-10 text-center text-sm text-muted-foreground">No runs yet.</div>}
              </div>
            </section>
          </div>

          <div className="space-y-4">
            <h2 className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">Rule & delivery</h2>

            {!editing ? (
              <div className="space-y-3 rounded-xl border border-border/60 bg-card/40 p-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Method</span>
                  <Badge variant="outline" className="text-[10px] text-accent border-accent/30">{methodLabel(alert.method)}</Badge>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Rule</span>
                  <span className="text-right">{describeConfig(alert.method, alert.config)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Severity</span>
                  <span>{alert.severity_override || "auto"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Cooldown</span>
                  <span>{alert.cooldown_minutes}m</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Schedule</span>
                  <span className="font-mono text-xs">{alert.cron} · {alert.timezone}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Delivery</span>
                  <span className="inline-flex items-center gap-1"><Channel className="h-3.5 w-3.5 text-accent" /> {alert.delivery}</span>
                </div>
                {!!alert.recipients?.length && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-muted-foreground">To</span>
                    <span className="truncate text-right text-xs">{alert.recipients.join(", ")}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4 rounded-xl border border-border/60 bg-card/40 p-4">
                <div className="space-y-1.5">
                  <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Method</Label>
                  <Select value={draft.method} onValueChange={(m) => setDraft({ ...draft, method: m, config: DEFAULT_CONFIG[m] })}>
                    <SelectTrigger data-testid={ALERTS.editMethod} className="bg-card/60"><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <AlertMethodFields method={draft.method} config={draft.config} onChange={(c) => setDraft({ ...draft, config: c })} />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Severity</Label>
                    <Select value={draft.severity} onValueChange={(v) => setDraft({ ...draft, severity: v })}>
                      <SelectTrigger className="bg-card/60"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto</SelectItem>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground">Cooldown (min)</Label>
                    <Input type="number" value={draft.cooldown} onChange={(e) => setDraft({ ...draft, cooldown: e.target.value })} className="bg-card/60" />
                  </div>
                </div>
                <div className="border-t border-border/40 pt-3">
                  <ScheduleForm value={draft.schedule} onChange={(s) => setDraft({ ...draft, schedule: s })} />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditing(false)} className="rounded-full">Cancel</Button>
                  <Button size="sm" data-testid={ALERTS.editSave} onClick={saveEdit} disabled={saving}
                    className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                    {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      <PromoteSignalDialog
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
        workspaceId={alert.workspace_id}
        source="alert"
        alertId={alert.id}
        sourceRefId={latestBreach?.id}
        defaultName={alert.name}
        defaultSummary={latestBreach?.detail || ""}
        evidence={promoteEvidence}
        ruleSnapshot={{
          method: alert.method, config: alert.config, cron: alert.cron,
          timezone: alert.timezone, prompt: alert.prompt,
        }}
      />
    </div>
  );
}
